/*
 * Shared list behaviour: search, sorting, paging and filters that survive
 * navigation.
 *
 * Every list screen re-implemented its own `search` field and let v-data-table
 * reset to page 1 on every visit, so an admin working through a long list lost
 * their place each time they opened a record. This keeps the table's state per
 * screen in localStorage and debounces the search so filtering a few thousand
 * rows does not re-run on every keystroke.
 *
 * Usage:
 *
 *   import tableState from "@/mixins/tableState";
 *   export default {
 *     mixins: [tableState],
 *     data: () => ({ tableStateKey: "buses" }),
 *   }
 *
 * then bind in the template:
 *
 *   <v-data-table
 *     :options.sync="tableOptions"
 *     :search="debouncedSearch"
 *     :footer-props="footerProps" />
 */

const STORAGE_PREFIX = "tableState:";
const SEARCH_DEBOUNCE_MS = 250;

export default {
  data() {
    return {
      //override in the component to namespace the saved state
      tableStateKey: null,

      search: "",
      debouncedSearch: "",
      searchTimer: null,

      tableOptions: {
        page: 1,
        itemsPerPage: 25,
        sortBy: [],
        sortDesc: [],
      },
    };
  },

  computed: {
    footerProps() {
      return {
        itemsPerPageText: this.$t("rows_per_page"),
        //"All" is last on purpose: it is the slow option on a long list
        itemsPerPageOptions: [10, 25, 50, 100, -1],
        showFirstLastPage: true,
      };
    },
  },

  watch: {
    search(value) {
      //debounce so typing does not re-filter the whole collection per keystroke
      if (this.searchTimer !== null) {
        window.clearTimeout(this.searchTimer);
      }
      this.searchTimer = window.setTimeout(() => {
        this.debouncedSearch = value;
        //a new search always starts from the first page
        this.tableOptions = { ...this.tableOptions, page: 1 };
      }, SEARCH_DEBOUNCE_MS);
    },

    tableOptions: {
      deep: true,
      handler() {
        this.persistTableState();
      },
    },
  },

  created() {
    this.restoreTableState();
  },

  beforeDestroy() {
    if (this.searchTimer !== null) {
      window.clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }
  },

  methods: {
    tableStorageKey() {
      return STORAGE_PREFIX + (this.tableStateKey || this.$route.name || "default");
    },

    persistTableState() {
      if (!this.tableStateKey && !this.$route) return;
      try {
        window.localStorage.setItem(
          this.tableStorageKey(),
          JSON.stringify({
            itemsPerPage: this.tableOptions.itemsPerPage,
            sortBy: this.tableOptions.sortBy,
            sortDesc: this.tableOptions.sortDesc,
          })
        );
      } catch (e) {
        //a full or disabled localStorage must never break the table
      }
    },

    restoreTableState() {
      try {
        const raw = window.localStorage.getItem(this.tableStorageKey());
        if (!raw) return;
        const saved = JSON.parse(raw);
        this.tableOptions = {
          ...this.tableOptions,
          itemsPerPage:
            saved.itemsPerPage === undefined || saved.itemsPerPage === null
              ? this.tableOptions.itemsPerPage
              : saved.itemsPerPage,
          sortBy: saved.sortBy || [],
          sortDesc: saved.sortDesc || [],
        };
      } catch (e) {
        //ignore malformed saved state
      }
    },

    clearSearch() {
      this.search = "";
      this.debouncedSearch = "";
    },
  },
};
